import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    deleteTask, restoreTask, duplicateTask, createFollowUpTask, mergeTasks, setTaskVisibility, updateTask,
    type Task,
} from '../../../packages/api-client/pm-api';
import { createTemplate } from '../../../packages/api-client/templates-api';
import { getErrorMessage } from '../../../packages/api-client';
import { Dialog } from '../../../packages/ui';
import { Copy, Save, Link2, GitMerge, Globe, Printer, Trash2, Check, MoreHorizontal } from 'lucide-react';

// ─── Actions menu ────────────────────────────────────────────────────────────

/** primary: shown as a button in the page header; the rest live in the "More actions" menu. */
interface TaskAction { label: string; icon: React.ReactNode; action: () => void; danger?: boolean; primary?: boolean; hint?: string; allowed: boolean }

/** onClose(true) returns focus to the trigger (keyboard close, item chosen);
 *  onClose(false) leaves it where the user clicked. */
function ActionsMenu({ x, y, actions, onClose }: { x: number; y: number; actions: TaskAction[]; onClose: (returnFocus: boolean) => void }) {
    const ref = useRef<HTMLDivElement>(null);

    // Focus the first item once, on open — not on every parent re-render.
    useEffect(() => {
        ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }, []);

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose(false);
        }
        window.addEventListener('mousedown', handler);
        return () => window.removeEventListener('mousedown', handler);
    }, [onClose]);

    // Clamp to viewport so the menu never clips off-screen right or bottom.
    const [pos, setPos] = React.useState({ left: x, top: y });
    useEffect(() => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        setPos({
            left: rect.right > window.innerWidth ? Math.max(0, x - rect.width) : x,
            top: rect.bottom > window.innerHeight ? Math.max(0, y - rect.height) : y,
        });
    }, [x, y]);

    function onKeyDown(e: React.KeyboardEvent) {
        // Escape closes; Tab closes and lets focus continue on from the trigger.
        if (e.key === 'Escape' || e.key === 'Tab') { onClose(true); return; }
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        const i = items.indexOf(document.activeElement as HTMLElement);
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
    }

    const safe = actions.filter(a => !a.danger);
    const danger = actions.filter(a => a.danger);
    const item = (a: TaskAction) => (
        <button key={a.label} type="button" role="menuitem" className={`context-menu-item${a.danger ? ' danger' : ''}`}
            onClick={() => { onClose(true); a.action(); }}>
            <span aria-hidden>{a.icon}</span> {a.label}
        </button>
    );
    return (
        <div ref={ref} role="menu" aria-label="Task actions" className="context-menu" style={{ top: pos.top, left: pos.left }} onKeyDown={onKeyDown}>
            {safe.map(item)}
            {danger.length > 0 && <div className="context-menu-divider" role="separator" />}
            {danger.map(item)}
        </div>
    );
}

// ─── Task actions (§7): header buttons + overflow menu, follow-up / merge dialogs, delete/restore ──

/** Each action is offered only when the backend route it calls would allow it (tasks:create/update/delete). */
export function useTaskActions({ id, task, setTask, perms }: {
    id: string | undefined;
    task: Task | null;
    setTask: React.Dispatch<React.SetStateAction<Task | null>>;
    perms: { update: boolean; create: boolean; remove: boolean };
}) {
    const navigate = useNavigate();
    const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
    const triggerRef = useRef<HTMLElement | null>(null);
    const [mergeTarget, setMergeTarget] = useState('');
    const [followUpTitle, setFollowUpTitle] = useState('');
    const [showMerge, setShowMerge] = useState(false);
    const [showFollowUp, setShowFollowUp] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [busy, setBusy] = useState(false);

    // Every action reports a failure instead of leaving an unhandled rejection.
    async function run(fn: () => Promise<void>) {
        setBusy(true);
        try { await fn(); } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
    }

    const handleDelete = () => {
        if (!id || !confirm('Delete this task? It disappears from lists; you can restore it from the next screen.')) return;
        return run(async () => { await deleteTask(id); setDeleted(true); });
    };
    const handleRestore = () => id && run(async () => { setTask(await restoreTask(id)); setDeleted(false); toast.success('Task restored'); });
    const handleDuplicate = () => id && run(async () => { const dup = await duplicateTask(id); navigate(`/tasks/${dup.id}`); });
    const handleFollowUp = () => id && followUpTitle.trim() && run(async () => {
        const t = await createFollowUpTask(id, { title: followUpTitle });
        setShowFollowUp(false);
        navigate(`/tasks/${t.id}`);
    });
    const handleMerge = () => id && mergeTarget.trim() && run(async () => {
        await mergeTasks(id, mergeTarget.trim());
        setShowMerge(false);
        navigate(`/tasks/${mergeTarget.trim()}`);
    });
    const handleMarkDone = () => id && run(async () => { setTask(await updateTask(id, { status: 'done' })); toast.success('Marked done'); });
    const handleTogglePublic = () => id && task && run(async () => setTask(await setTaskVisibility(id, !task.is_public)));
    const handleSaveAsTemplate = () => task && run(async () => {
        await createTemplate({
            name: task.title,
            description: task.description ?? '',
            priority: task.priority ?? 'none',
            estimated_hours: task.estimated_hours,
        });
        toast.success(`Saved "${task.title}" as a template`);
    });

    function openCtx(e: React.MouseEvent) {
        e.preventDefault();
        triggerRef.current = e.currentTarget as HTMLElement;
        const rect = triggerRef.current.getBoundingClientRect();
        setCtx({ x: rect.left, y: rect.bottom + 4 });
    }

    function closeMenu(returnFocus: boolean) {
        setCtx(null);
        if (returnFocus) triggerRef.current?.focus();
    }

    const actions: TaskAction[] = task ? ([
        { icon: <Check size={16} />, label: 'Mark done', action: handleMarkDone, primary: true, hint: 'Set the status to Done.', allowed: perms.update && task.status !== 'done' },
        { icon: <Copy size={16} />, label: 'Duplicate', action: handleDuplicate, hint: 'Copy this task into the same project.', allowed: perms.create },
        // The templates route only needs a session; gated on tasks:create so a view-only reader gets no create actions.
        { icon: <Save size={16} />, label: 'Save as Template', action: handleSaveAsTemplate, hint: 'Reuse its title, description, priority and estimate.', allowed: perms.create },
        { icon: <Link2 size={16} />, label: 'Create Follow-up', action: () => setShowFollowUp(true), primary: true, hint: 'A new task linked back to this one.', allowed: perms.create },
        { icon: <GitMerge size={16} />, label: 'Merge into…', action: () => setShowMerge(true), hint: 'Move its work to another task, then delete it.', allowed: perms.update },
        { icon: <Globe size={16} />, label: task.is_public ? 'Make Private' : 'Make Public', action: handleTogglePublic, hint: task.is_public ? 'Turn off the public read-only link.' : 'Anyone with the link can read it.', allowed: perms.update },
        { icon: <Printer size={16} />, label: 'Print', action: () => window.print(), hint: 'Print or save as PDF.', allowed: true },
        { icon: <Trash2 size={16} />, label: 'Delete', action: handleDelete, danger: true, hint: 'Soft delete; restorable.', allowed: perms.remove },
    ] satisfies TaskAction[]).filter(a => a.allowed) : [];

    // Delete is never primary: it and the rarer actions sit in the overflow menu, destructive last.
    const overflow = actions.filter(a => !a.primary);
    const contextMenu = ctx && <ActionsMenu x={ctx.x} y={ctx.y} actions={overflow} onClose={closeMenu} />;

    // Header actions: the primary buttons, then the "More actions" menu trigger.
    const headerActions = actions.length > 0 && (
        <div role="group" aria-label="Task actions" className="task-actions-bar">
            {actions.filter(a => a.primary).map((a, i) => (
                <button key={a.label} type="button" className={`btn btn-${i === 0 ? 'primary' : 'secondary'} btn-sm task-inline`} onClick={a.action} disabled={busy} title={a.hint}>
                    <span aria-hidden>{a.icon}</span> {a.label}
                </button>
            ))}
            {overflow.length > 0 && (
                <button type="button" className="btn btn-ghost btn-sm task-inline" aria-haspopup="menu" aria-expanded={ctx !== null} onContextMenu={openCtx} onClick={openCtx}>
                    <MoreHorizontal size={16} aria-hidden /> More actions
                </button>
            )}
        </div>
    );

    const modals = (
        <>
            <Dialog open={showFollowUp} onClose={() => setShowFollowUp(false)} title="Create follow-up task" closeOnBackdrop={false}
                footer={<>
                    <button type="button" className="btn btn-secondary btn-md" onClick={() => setShowFollowUp(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-md" onClick={handleFollowUp} disabled={busy || !followUpTitle.trim()}>Create</button>
                </>}>
                <div className="form-group">
                    <label className="form-label" htmlFor="follow-up-title">Title *</label>
                    <input id="follow-up-title" className="form-input" autoFocus placeholder="Follow-up task title…"
                        value={followUpTitle} onChange={e => setFollowUpTitle(e.target.value)} />
                </div>
            </Dialog>

            <Dialog open={showMerge} onClose={() => setShowMerge(false)} title="Merge task into…" closeOnBackdrop={false}
                footer={<>
                    <button type="button" className="btn btn-secondary btn-md" onClick={() => setShowMerge(false)}>Cancel</button>
                    <button type="button" className="btn btn-danger btn-md" onClick={handleMerge} disabled={busy || !mergeTarget.trim()}>Merge & Delete</button>
                </>}>
                {/* Mirrors tasks repo Merge: subtasks and comments move; tags move
                    where the target project has a tag of the same name; this task
                    is then soft-deleted. */}
                <p className="task-muted">
                    Subtasks and comments move to the target task. Tags move where the target's project has a tag with the same name. Subtasks moved into another project lose their milestone and phase. This task is then deleted.
                </p>
                <div className="form-group">
                    <label className="form-label" htmlFor="merge-target">Target task ID *</label>
                    <input id="merge-target" className="form-input" autoFocus placeholder="Paste target task ID…"
                        value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} />
                </div>
            </Dialog>
        </>
    );

    return { contextMenu, headerActions, modals, deleted, handleRestore, busy };
}
